import {
  useAllFeePeriodsQuery,
  useLinkBenefitToRawTransactionMutation,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import {BenefitPicker} from 'src/CreateTransactionForBenefitModal/BenefitPicker';
import {Modal} from 'src/Modal';
import {evictFeePeriodQueries} from 'src/UnlinkBenefitTransactionModal';
import type {SystemProps} from 'src/Modals';

export type LinkBenefitModalUserProps = {
  rawTransaction: {_id: string; date: string; account: {_id: string}};
  /** Name of the currently linked benefit, when changing an existing link */
  currentBenefitName?: string | null;
};

/**
 * Picker for linking a raw transaction to a card benefit. Linking applies
 * immediately (it is its own mutation, not part of the raw transaction
 * update payload), so the modal closes on selection with no Save step.
 */
export const LinkBenefitModal = (
  props: LinkBenefitModalUserProps & SystemProps,
) => {
  const {rawTransaction, currentBenefitName, onRequestClose} = props;
  const {data: feePeriodsData, loading: feePeriodsLoading} =
    useAllFeePeriodsQuery();
  const [linkBenefit, {loading: linking}] =
    useLinkBenefitToRawTransactionMutation({update: evictFeePeriodQueries});
  const [error, setError] = React.useState<string | null>(null);

  const handleSelect = (selected: {_id: string}) => {
    // BenefitPicker has no disabled state, so guard here: a second selection
    // while a mutation is in flight would queue conflicting link calls
    if (linking) return;
    setError(null);
    linkBenefit({
      variables: {
        rawTransactionId: rawTransaction._id,
        benefitId: selected._id,
      },
    })
      .then(() => onRequestClose())
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to link benefit.');
      });
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="md"
      header={currentBenefitName ? 'Change benefit' : 'Link benefit'}
      footer={
        <Button variant="outline" onClick={onRequestClose}>
          Cancel
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {currentBenefitName && (
          <p className="text-muted-foreground text-sm">
            Currently linked to {currentBenefitName}. Selecting a benefit
            replaces the link.
          </p>
        )}
        {feePeriodsLoading ? (
          <p className="text-muted-foreground text-sm">Loading benefits…</p>
        ) : (
          <BenefitPicker
            feePeriods={feePeriodsData?.allFeePeriods ?? []}
            transactionDate={parseDate(rawTransaction.date).toSeconds()}
            accountId={rawTransaction.account._id}
            onSelect={handleSelect}
          />
        )}
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
    </Modal>
  );
};
